import { ActionTree, MutationTree, GetterTree } from 'vuex';
import { ManagerState, Manager, IBusiness } from './managerTypes';
import { IRouterProps } from '@/router/interfaces/router-props.interface';
import { RootState } from '../types';
import APIService from '@/api';
import Module from '../interfaces/module.interface';

const service = new APIService();

export const state: ManagerState = new ManagerState({}, []);

const factory = async (context: IRouterProps) => {
  if (context.auth === 'manager') {
    // Skip if already initialized by initAuthState() in main.ts
    if (APIService.token === 'cookie-session' && context.store.getters['manager/getExtraInfo']) {
      return;
    }

    // Auth is now cookie-based — call /auth/me to restore claims instead of
    // decoding a token from localStorage.
    try {
      const response = await service.get('/auth/me');
      if (response && response.data) {
        const { user, extraInfo } = response.data;
        context.store.dispatch('manager/SET_MANAGER', {
          sub: user && user.managerId,
          username: user && user.managerId,
          extraInfo,
        });
        // Signal to AuthorizationService.userIsValid() that the cookie session
        // is active. The actual JWT stays in the HttpOnly cookie.
        APIService.token = 'cookie-session';
      } else {
        APIService.token = '';
      }
    } catch (_e) {
      // Not authenticated — the router guard will redirect to login
      APIService.token = '';
    }
  }
};

const actions: ActionTree<ManagerState, RootState> = {
  SET_MANAGER(context, payload: ManagerState) {
    context.commit('setManager', payload);
  },
  SET_BUSINESS_LIST(context, payload: IBusiness[]) {
    context.commit('setBusinessList', payload);
  },
};

const mutations: MutationTree<ManagerState> = {
  setManager(state, payload: Manager) {
    state.info = payload;
  },
  setBusinessList(state, payload: IBusiness[]) {
    state.business = payload;
  },
};

const getters: GetterTree<ManagerState, RootState> = {
  getRequireBusiness(state) {
    return state.info.extraInfo ? state.info.extraInfo.requireBusiness : null;
  },
  getRole(state) {
    return state.info.extraInfo ? state.info.extraInfo.role : null;
  },
  getExtraInfo(state) {
    return state.info.extraInfo;
  },
  isImpersonated(state) {
    return state.info.extraInfo ? !!state.info.extraInfo.isImpersonated : false;
  },
  hasTwismWalletAccess(state) {
    return state.info.extraInfo
      ? !!state.info.extraInfo.hasTwismWalletAccess
      : false;
  },
  getPermissions(state) {
    return (state.info.extraInfo && state.info.extraInfo.permissions) || [];
  },
  getBusinessList(state) {
    return state.business;
  },
};

const namespaced: boolean = true;

export const manager: Module<ManagerState, RootState> = {
  namespaced,
  initializer: {
    factory,
    on: 'BeforeEachRoute',
    provide: 'ManagerStore',
  },
  state,
  actions,
  mutations,
  getters,
};
